import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { motion } from "framer-motion";

// ─── Recipes ───
const recipes = [
  {
    name: "عصير البرتقال 🍊",
    emoji: "🧃",
    steps: [
      { instruction: "أولاً: اختر الفاكهة الصحيحة!", options: ["🍊 برتقال", "🍎 تفاح", "🍇 عنب"], answer: "🍊 برتقال", emoji: "🍊" },
      { instruction: "ثانياً: ماذا نحتاج لعصره؟", options: ["🔪 سكين", "🍋 عصارة", "🥄 ملعقة"], answer: "🍋 عصارة", emoji: "🍋" },
      { instruction: "ثالثاً: نضيف...", options: ["🧂 ملح", "🍬 سكر", "🌶️ فلفل"], answer: "🍬 سكر", emoji: "🍬" },
      { instruction: "أخيراً: نقدمه في...", options: ["🍽️ صحن", "🥤 كوب", "🍲 طنجرة"], answer: "🥤 كوب", emoji: "🥤" },
    ],
  },
  {
    name: "سلطة الفواكه 🥗",
    emoji: "🍓",
    steps: [
      { instruction: "أولاً: اختر الفاكهة!", options: ["🍓 فراولة", "🥩 لحم", "🧀 جبن"], answer: "🍓 فراولة", emoji: "🍓" },
      { instruction: "ثانياً: نقطع بـ...", options: ["🔪 سكين", "🥄 ملعقة", "🖊️ قلم"], answer: "🔪 سكين", emoji: "🔪" },
      { instruction: "ثالثاً: نضيف فاكهة أخرى!", options: ["🍌 موز", "🧅 بصل", "🥔 بطاطس"], answer: "🍌 موز", emoji: "🍌" },
      { instruction: "أخيراً: نزيّن بـ...", options: ["🍫 شوكولاتة", "🧂 ملح", "🌶️ فلفل"], answer: "🍫 شوكولاتة", emoji: "🍫" },
    ],
  },
  {
    name: "ساندويتش 🥪",
    emoji: "🍞",
    steps: [
      { instruction: "أولاً: نبدأ بـ...", options: ["🍞 خبز", "🍕 بيتزا", "🍰 كيك"], answer: "🍞 خبز", emoji: "🍞" },
      { instruction: "ثانياً: نضع...", options: ["🧀 جبن", "🍬 حلوى", "🧊 ثلج"], answer: "🧀 جبن", emoji: "🧀" },
      { instruction: "ثالثاً: نضيف...", options: ["🥬 خس", "🍭 مصاصة", "🧁 كب كيك"], answer: "🥬 خس", emoji: "🥬" },
      { instruction: "أخيراً: نضع خبز فوق ونقطع!", options: ["🔪 نقطع نص", "🗑️ نرميه", "🔥 نحرقه"], answer: "🔪 نقطع نص", emoji: "🔪" },
    ],
  },
];

interface Props {
  character: string;
  characterName: string;
  onFinish: (score: number, maxScore: number) => void;
  onCorrect: () => void;
}

export default function CookingGame({ character, characterName, onFinish, onCorrect }: Props) {
  const [recipeIdx, setRecipeIdx] = useState(0);
  const [stepIdx, setStepIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  const [completedRecipes, setCompletedRecipes] = useState(0);
  const [collectedEmojis, setCollectedEmojis] = useState<string[]>([]);

  const recipe = recipes[recipeIdx];
  const step = recipe?.steps[stepIdx];
  const totalRecipes = recipes.length;

  const handleAnswer = (answer: string) => {
    const correct = answer === step.answer;
    setSelectedAnswer(answer);
    setIsCorrect(correct);
    if (correct) {
      setScore(s => s + 1);
      setCollectedEmojis(prev => [...prev, step.emoji]);
      onCorrect();
    }
    setTimeout(() => {
      const nextStep = stepIdx + 1;
      if (nextStep >= recipe.steps.length) {
        const next = completedRecipes + 1;
        setCompletedRecipes(next);
        if (next >= totalRecipes) {
          onFinish(score + (correct ? 1 : 0), totalRecipes * 4);
        } else {
          setRecipeIdx(recipeIdx + 1);
          setStepIdx(0);
          setCollectedEmojis([]);
          setSelectedAnswer(null);
          setIsCorrect(null);
        }
      } else {
        setStepIdx(nextStep);
        setSelectedAnswer(null);
        setIsCorrect(null);
      }
    }, 1200);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
      <div className="flex items-center gap-2">
        <Badge className="bg-orange-100 text-orange-800 border-orange-300 text-sm">
          الوصفة: {recipeIdx + 1} / {totalRecipes}
        </Badge>
        <Badge variant="outline" className="text-sm">
          الخطوة: {stepIdx + 1} / {recipe.steps.length}
        </Badge>
      </div>
      <Card className="bg-white/90 backdrop-blur-sm border-2 border-orange-200 shadow-xl rounded-2xl">
        <CardContent className="p-5 space-y-4 text-center">
          <div className="flex items-center justify-center gap-3">
            <img src={character} alt={characterName} className="w-14 h-14 rounded-full border-2 border-orange-300 bg-orange-50 object-cover" />
            <div>
              <p className="text-sm font-semibold text-orange-700">نطبخ مع {characterName}!</p>
              <p className="text-lg font-bold text-foreground">{recipe.name}</p>
            </div>
          </div>

          {/* Collected ingredients */}
          {collectedEmojis.length > 0 && (
            <div className="flex justify-center gap-2 bg-orange-50 rounded-xl p-3">
              <span className="text-xs text-orange-600 font-semibold self-center">المكونات:</span>
              {collectedEmojis.map((e, i) => (
                <motion.span key={i} initial={{ scale: 0 }} animate={{ scale: 1 }} className="text-2xl">{e}</motion.span>
              ))}
            </div>
          )}

          {/* Progress bar for recipe */}
          <Progress value={((stepIdx + 1) / recipe.steps.length) * 100} className="h-2 rounded-full" />

          <motion.p key={`${recipeIdx}-${stepIdx}`} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="text-lg font-bold text-foreground py-2">
            {step?.instruction}
          </motion.p>

          <div className="grid gap-2">
            {step?.options.map((opt) => {
              const isSelected = selectedAnswer === opt;
              const isCorrectOpt = opt === step.answer;
              let cls = "bg-orange-50 border-orange-200 text-orange-800 hover:bg-orange-100";
              if (selectedAnswer) {
                if (isCorrectOpt) cls = "bg-green-200 border-green-400 text-green-900";
                else if (isSelected) cls = "bg-red-200 border-red-400 text-red-900";
              }
              return (
                <button key={opt} onClick={() => !selectedAnswer && handleAnswer(opt)}
                  disabled={!!selectedAnswer}
                  className={`p-4 rounded-xl border-2 text-lg font-bold transition-all active:scale-95 ${cls}`}>
                  {opt}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
